import { CSSProperties, FunctionComponent, useState, useEffect, useMemo } from 'react';
import React from 'react';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { LinearProgress, Typography, IconButton, Avatar, Badge, Checkbox, Chip, Tooltip, Snackbar, Dialog, Table, TableRow, TableCell, List, ListItem, ListItemAvatar, ListItemText } from '../../tailwindcomponents';
import { Button } from '../../tailwindcomponents';
import { MxiTask, TaskNote, LabourAssignedByTaskBarcode } from '../../types/tasks';
import { User } from '../../types/user';
import { redirectToMxi } from '../../config/mxiProvider';
import { redirectToIdmrTask, redirectToIdmrWorkPackage } from '../../config/idmrProvider';
import crewPlanApi from '../../api/crewPlan';
import tasksApi from '../../api/tasks';
import PartDetails from '../../components/Parts/PartDetails';
import { EmployeeSearch, Collection } from '../../components';
import { ShoppingCart, Visibility } from '../../components/icons';
import { isBoeingAircraft } from '../../utils/aircraftUtils';
import RemarksCell from './RemarksCell';
import hangarPlanApi from '../../api/hangarPlanApi';
import { palette } from '../../theme/palette';

type AlertColor = 'success' | 'info' | 'warning' | 'error';

// Business rule: which tasks may have their labour edited in bulk.
// Centralised so future rules (status checks, role checks) are easy to extend.
const canEditTaskLabour = (task: MxiTask): boolean => {
    return task.status !== "HEADER";
};

type StatusValue = 'ACTV' | 'IN WORK' | 'PAUSE' | 'COMPLETE' | 'UNASSIGN';

// Same predicate the legend counts use, so a chip's count always equals the rows it adds.
const matchesStatus = (task: MxiTask, value: StatusValue): boolean =>
    value === 'UNASSIGN' ? task.unassigned === true : task.status === value;

// Several statuses can be selected at once (a task shows if it matches any of them);
// nothing selected means "All".
const matchesStatusFilters = (task: MxiTask, filters: ReadonlySet<StatusValue>): boolean =>
    filters.size === 0 || Array.from(filters).some(value => matchesStatus(task, value));

// Concurrency-limited mapper. Used as a frontend fallback while a proper
// backend bulk labour endpoint (AssignLabourToTasks / RemoveLabourFromTasks /
// ClearLabourFromTasks) does not yet exist. Prefer a real backend bulk
// endpoint when available — see comments on the bulk handlers below.
const runWithConcurrency = async <T, R>(
    items: T[],
    limit: number,
    worker: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
    const results: R[] = new Array(items.length);
    let cursor = 0;
    const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
        while (true) {
            const current = cursor++;
            if (current >= items.length) return;
            results[current] = await worker(items[current], current);
        }
    });
    await Promise.all(runners);
    return results;
};

const BULK_CONCURRENCY = 6;

type Props = {
    dialogOpen: boolean,
    selectedEvent: MbscCalendarEvent,
    onClose: Function;
    isAdmin: boolean;
    location: string;
}

const ReturnStatusColor = (status: string, unassigned: boolean | undefined) => {

    if (unassigned === true) {
        return palette.status.unassigned
    }
    switch (status) {
        case ("COMPLETE"):
            return palette.success;
        case ("ACTV"):
            return palette.secondary;
        case ("PAUSE"):
            return "";
        case ("COMMIT"):
            return palette.status.commit;
        default:
            return palette.status.inwork;
    }
}

const getStatusBorderProperties = (status: string) => {
    if (status === "PAUSE") {
        return {
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: "black",
        };
    }

    return {};
};

/**
 * A Chip that behaves like a button.
 *
 * MXI and eSign used to be IconButtons, which is why call-site `text-*` classes had no
 * effect on them: IconButton sets `font-size` as an INLINE style (so no utility can win)
 * and `.ds-icon-btn` is a fixed `--ds-control-h` square (so padding utilities are dead
 * too). They are text labels, not glyphs, so they belong in a chip - `.ds-chip` sizes its
 * text from --ds-fs-xs, a step below the table's --ds-fs-sm.
 *
 * Chip renders a <div>, so the role, tab stop and Enter/Space handling are supplied here:
 * these replaced real <button> elements and have to stay reachable without a mouse.
 */
const ActionChip: FunctionComponent<{
    onClick: () => void;
    label: string;
    color?: 'default' | 'primary';
    style?: CSSProperties;
}> = ({ onClick, label, color = 'default', style }) => (
    <Chip
        size="small"
        color={color}
        label={label}
        style={style}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        }}
    />
);

const STATUS_FILTERS: { value: StatusValue, label: string, dotColor: string }[] = [
    { value: 'ACTV', label: 'ACTV', dotColor: palette.secondary },
    { value: 'IN WORK', label: 'In Work', dotColor: palette.status.inwork },
    { value: 'PAUSE', label: 'Pause', dotColor: 'black' },
    { value: 'COMPLETE', label: 'Complete', dotColor: palette.success },
    { value: 'UNASSIGN', label: 'Unassign', dotColor: palette.status.unassigned },
];

/**
 * The status legend, doubling as the task filter: one chip per status with its count.
 *
 * The frozen status hue rides on the dot (`.ds-chip-dot`) while the chip itself stays themed,
 * so the label keeps its contrast in light and dark and the colour is never the only signal.
 * Statuses are toggles and can be combined; "All" is lit while none is selected and clears
 * the selection when clicked. Selected chips take the accent tone; a status with no tasks
 * is dimmed but still selectable so the empty result is explained rather than the chip vanishing.
 */
const StatusFilterChips: FunctionComponent<{
    tasks: MxiTask[];
    selected: ReadonlySet<StatusValue>;
    onToggle: (value: StatusValue) => void;
    onClear: () => void;
}> = ({ tasks, selected, onToggle, onClear }) => {
    const chip = (key: string, label: string, count: number, isSelected: boolean, onActivate: () => void, dotColor?: string) => (
        <Chip
            key={key}
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
            color={isSelected ? 'primary' : 'default'}
            className="transition-opacity"
            style={{ opacity: count === 0 && !isSelected ? 0.5 : undefined, fontWeight: isSelected ? 600 : undefined }}
            onClick={onActivate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); }
            }}
            label={
                <>
                    {dotColor !== undefined && <span className="ds-chip-dot" style={{ backgroundColor: dotColor }} />}
                    {`${label} (${count})`}
                </>
            }
        />
    );

    return (
        <div role="group" aria-label="Filter tasks by status" className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
            {chip('ALL', 'All', tasks.length, selected.size === 0, onClear)}
            {STATUS_FILTERS.map(({ value, label, dotColor }) =>
                chip(value, label, tasks.filter(t => matchesStatus(t, value)).length, selected.has(value), () => onToggle(value), dotColor)
            )}
        </div>
    );
};

// Map MXI priority codes (P01/P02/P03) to display labels (P1/P2/P3),
// mirroring the convention used in LinePlannerWorkPackageOverview.
// Unknown / missing codes return "" so the caller can skip rendering a badge,
// matching the behaviour in LinePlannerWorkPackageOverview and WorkPackageCalendar.
const formatTaskPriority = (priority: string | null | undefined): string => {
    if (priority === "P01") return "P1";
    if (priority === "P02") return "P2";
    if (priority === "P03") return "P3";
    return "";
};

const WorkPackageDetailsDialog: FunctionComponent<Props> = React.memo(({ dialogOpen, selectedEvent, onClose, isAdmin, location }) => {
    const [partDialogOpen, setPartDialogOpen] = useState(false);
    const [LabourDialogOpen, setLabourDialogOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<MxiTask>();
    const [tasksInWorkpackage, setTasksInWorkpackage] = useState<MxiTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [notesMap, setNotesMap] = useState<Record<string, TaskNote[]>>({});
    const [notesLoaded, setNotesLoaded] = useState(false);
    const [statusFilters, setStatusFilters] = useState<Set<StatusValue>>(new Set());

    // Bulk-labour state. Selection is stored as a Set of barcodes for O(1)
    // membership checks against potentially large task lists.
    const [selectedTaskBarcodes, setSelectedTaskBarcodes] = useState<Set<string>>(new Set());
    const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
    const [bulkRemoveDialogOpen, setBulkRemoveDialogOpen] = useState(false);
    const [clearAllLabourDialogOpen, setClearAllLabourDialogOpen] = useState(false);
    const [bulkSelectedEmployees, setBulkSelectedEmployees] = useState<User[]>([]);
    const [bulkRemoveEmployeeIds, setBulkRemoveEmployeeIds] = useState<Set<number>>(new Set());
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

    const showSnackbar = (message: string, severity: AlertColor = "success") => {
        setSnackbarSeverity(severity);
        setSnackbarMessage(message);
    };

    useEffect(() => {
        if (!selectedEvent?.id) return;        // guard first render
        setStatusFilters(new Set());
        setLoading(true);
        refreshWorkpackage(selectedEvent.id.toString());
    }, [selectedEvent]);

    const onSelectedEmployee = (employeeId: number) => {
        if (!employeeId) return;
        crewPlanApi.ChangeLabour(selectedTask!.barcode, employeeId, selectedTask!.wo_barcode).then(res => {
            refreshWorkpackage(selectedEvent.id!.toString());
        })
    }

    const fetchNotes = async (tasks: MxiTask[]) => {
        setNotesLoaded(false);
        setNotesMap({});

        if (tasks.length === 0) {
            setNotesLoaded(true);
            return;
        }
        
        try {
            const barcodes = tasks.map(task => task.barcode);
            const response = await tasksApi.GetTaskNotesByBarcodes(barcodes);

            if (response.status && response.data) {
                setNotesMap(response.data);
                return;
            }

            const fallbackNotes = await Promise.all(
                tasks.map(async task => {
                    try {
                        const taskResponse = await tasksApi.GetTaskNotes(task.barcode);
                        return [task.barcode, taskResponse.status ? taskResponse.data : []] as const;
                    } catch (taskError) {
                        console.error(`Error fetching notes for task ${task.barcode}:`, taskError);
                        return [task.barcode, []] as const;
                    }
                })
            );

            setNotesMap(Object.fromEntries(fallbackNotes));
        } catch (error) {
            console.error('Error fetching task notes:', error);
        } finally {
            setNotesLoaded(true);
        }
    };

    const refreshWorkpackage = (woBarcode: string) => {
        setLoading(true);
        hangarPlanApi.GetTasksByWorkpackageBarcode(woBarcode)
            .then(res => {
                if (res.status) {
                    setTasksInWorkpackage(res.data);
                    if (selectedTask) {
                        setSelectedTask(
                            res.data.find(t => t.barcode === selectedTask.barcode)
                        );
                    }
                    fetchNotes(res.data);
                }
            })
            .finally(() => setLoading(false));
    };


    const onLabourColumnClicked = (row: MxiTask) => {
        if (isAdmin) {
            setLabourDialogOpen(true); setSelectedTask(row);
        }
    }

    // ---------------------------------------------------------------------
    // Bulk labour: derived state
    // ---------------------------------------------------------------------
    // The bulk actions are scoped to what is on screen, so "Select all" and "Clear all labour"
    // never touch tasks hidden by the status filter.
    const visibleTasks = useMemo(
        () => tasksInWorkpackage.filter(t => matchesStatusFilters(t, statusFilters)),
        [tasksInWorkpackage, statusFilters]
    );

    const editableTasks = useMemo(
        () => visibleTasks.filter(canEditTaskLabour),
        [visibleTasks]
    );

    const applyStatusFilters = (next: Set<StatusValue>) => {
        setStatusFilters(next);
        // Drop selections that the new filter hides, so "N selected" only counts visible rows.
        const stillVisible = new Set(
            tasksInWorkpackage.filter(t => matchesStatusFilters(t, next)).map(t => t.barcode)
        );
        setSelectedTaskBarcodes(prev => new Set(Array.from(prev).filter(b => stillVisible.has(b))));
    };

    const toggleStatusFilter = (value: StatusValue) => {
        const next = new Set(statusFilters);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        applyStatusFilters(next);
    };

    const clearStatusFilters = () => {
        if (statusFilters.size > 0) applyStatusFilters(new Set());
    };

    const selectedTasks = useMemo(
        () => tasksInWorkpackage.filter(t => selectedTaskBarcodes.has(t.barcode)),
        [tasksInWorkpackage, selectedTaskBarcodes]
    );

    const selectedTaskCount = selectedTaskBarcodes.size;

    // Only count assignments on tasks the clear action would actually touch,
    // so the "Clear all labour" button doesn't enable for a guaranteed no-op.
    const clearableTasks = useMemo(
        () => editableTasks.filter(t => (t.assigned_employees?.length ?? 0) > 0),
        [editableTasks]
    );

    const hasAnyAssignedLabour = clearableTasks.length > 0;

    const allEditableSelected = editableTasks.length > 0 &&
        editableTasks.every(t => selectedTaskBarcodes.has(t.barcode));

    // For the bulk-remove dialog: unique employees currently assigned to the
    // selected tasks, with a count of how many selected tasks each is on.
    const assignedEmployeesInSelection = useMemo(() => {
        const map = new Map<number, { employee: LabourAssignedByTaskBarcode, taskCount: number }>();
        for (const task of selectedTasks) {
            for (const emp of task.assigned_employees ?? []) {
                const existing = map.get(emp.employee_id);
                if (existing) existing.taskCount += 1;
                else map.set(emp.employee_id, { employee: emp, taskCount: 1 });
            }
        }
        return Array.from(map.values()).sort((a, b) =>
            a.employee.employee_name.localeCompare(b.employee.employee_name)
        );
    }, [selectedTasks]);

    // Count only what will actually be removed by submitClearAllLabour
    // (editable tasks), so the confirmation dialog doesn't overstate impact.
    const totalClearableAssignments = useMemo(
        () => clearableTasks.reduce((sum, t) => sum + (t.assigned_employees?.length ?? 0), 0),
        [clearableTasks]
    );

    // ---------------------------------------------------------------------
    // Bulk labour: selection helpers
    // ---------------------------------------------------------------------
    const toggleTaskSelection = (taskBarcode: string) => {
        setSelectedTaskBarcodes(prev => {
            const next = new Set(prev);
            if (next.has(taskBarcode)) next.delete(taskBarcode);
            else next.add(taskBarcode);
            return next;
        });
    };

    const selectAllTasks = () => {
        setSelectedTaskBarcodes(new Set(editableTasks.map(t => t.barcode)));
    };

    const clearSelectedTasks = () => {
        setSelectedTaskBarcodes(new Set());
    };

    const handleSelectAllToggle = () => {
        if (allEditableSelected) clearSelectedTasks();
        else selectAllTasks();
    };

    // ---------------------------------------------------------------------
    // Bulk labour: open dialogs
    // ---------------------------------------------------------------------
    const openBulkAssignDialog = () => {
        if (selectedTaskCount === 0) {
            showSnackbar("Select at least one task.", "warning");
            return;
        }
        setBulkSelectedEmployees([]);
        setBulkAssignDialogOpen(true);
    };

    const openBulkRemoveDialog = () => {
        if (selectedTaskCount === 0) {
            showSnackbar("Select at least one task.", "warning");
            return;
        }
        setBulkRemoveEmployeeIds(new Set());
        setBulkRemoveDialogOpen(true);
    };

    const openClearAllLabourDialog = () => {
        if (!hasAnyAssignedLabour) {
            showSnackbar("There is no assigned labour to clear.", "info");
            return;
        }
        setClearAllLabourDialogOpen(true);
    };

    const addEmployeeToBulkSelection = (user: User | null) => {
        if (!user) return;
        setBulkSelectedEmployees(prev => {
            if (prev.some(e => e.user_id === user.user_id)) return prev; // no duplicates
            return [...prev, user];
        });
    };

    const removeEmployeeFromBulkSelection = (employeeId: number) => {
        setBulkSelectedEmployees(prev => prev.filter(e => e.user_id !== employeeId));
    };

    const toggleBulkRemoveEmployee = (employeeId: number) => {
        setBulkRemoveEmployeeIds(prev => {
            const next = new Set(prev);
            if (next.has(employeeId)) next.delete(employeeId);
            else next.add(employeeId);
            return next;
        });
    };

    // ---------------------------------------------------------------------
    // Bulk labour: submit
    //
    // Frontend fallback: the project does not yet expose a proper backend
    // bulk endpoint (AssignLabourToTasks / RemoveLabourFromTasks /
    // ClearLabourFromTasks). Until that exists we run the existing
    // crewPlanApi.ChangeLabour endpoint per (task, employee) pair with a
    // small concurrency limit. ChangeLabour toggles assignment server-side,
    // so we only call it when it would produce the intended direction
    // (assign / remove) based on the current client state.
    // ---------------------------------------------------------------------
    const submitBulkAssignLabour = async () => {
        if (selectedTaskCount === 0) {
            showSnackbar("Select at least one task.", "warning");
            return;
        }
        if (bulkSelectedEmployees.length === 0) {
            showSnackbar("Select at least one employee.", "warning");
            return;
        }

        const editableSelectedTasks = selectedTasks.filter(canEditTaskLabour);
        const skippedTaskCount = selectedTasks.length - editableSelectedTasks.length;

        type Job = { task: MxiTask, employeeId: number };
        const jobs: Job[] = [];
        for (const task of editableSelectedTasks) {
            const alreadyAssigned = new Set((task.assigned_employees ?? []).map(e => e.employee_id));
            for (const emp of bulkSelectedEmployees) {
                if (!alreadyAssigned.has(emp.user_id)) {
                    jobs.push({ task, employeeId: emp.user_id });
                }
            }
        }

        if (jobs.length === 0) {
            showSnackbar(
                skippedTaskCount > 0
                    ? "All selected employees were already assigned to the editable selected tasks."
                    : "All selected employees are already assigned to the selected tasks.",
                "info"
            );
            return;
        }

        setBulkActionLoading(true);
        const results = await runWithConcurrency(jobs, BULK_CONCURRENCY, async (job) => {
            try {
                const res = await crewPlanApi.ChangeLabour(job.task.barcode, job.employeeId, job.task.wo_barcode);
                return res?.status === true;
            } catch {
                return false;
            }
        });
        setBulkActionLoading(false);

        const succeeded = results.filter(Boolean).length;
        const failed = results.length - succeeded;
        const affectedTaskBarcodes = new Set(
            jobs.filter((_, i) => results[i]).map(j => j.task.barcode)
        );

        if (failed === 0) {
            const parts: string[] = [
                `Successfully assigned ${bulkSelectedEmployees.length} employee(s) to ${affectedTaskBarcodes.size} task(s).`
            ];
            if (skippedTaskCount > 0) {
                parts.push(`Some selected tasks were skipped because they cannot be edited (${skippedTaskCount}).`);
            }
            showSnackbar(parts.join(" "), "success");
        } else {
            showSnackbar(
                `Could not update labour for ${failed} of ${jobs.length} assignment(s). Please review and try again.`,
                "warning"
            );
        }

        setBulkAssignDialogOpen(false);
        setBulkSelectedEmployees([]);
        clearSelectedTasks();
        refreshWorkpackage(selectedEvent.id!.toString());
    };

    const submitBulkRemoveLabour = async () => {
        if (selectedTaskCount === 0) {
            showSnackbar("Select at least one task.", "warning");
            return;
        }
        if (bulkRemoveEmployeeIds.size === 0) {
            showSnackbar("Select at least one employee.", "warning");
            return;
        }

        const editableSelectedTasks = selectedTasks.filter(canEditTaskLabour);
        const skippedTaskCount = selectedTasks.length - editableSelectedTasks.length;

        type Job = { task: MxiTask, employeeId: number };
        const jobs: Job[] = [];
        for (const task of editableSelectedTasks) {
            for (const emp of task.assigned_employees ?? []) {
                if (bulkRemoveEmployeeIds.has(emp.employee_id)) {
                    jobs.push({ task, employeeId: emp.employee_id });
                }
            }
        }

        if (jobs.length === 0) {
            showSnackbar("None of the selected employees are assigned to the selected tasks.", "info");
            return;
        }

        setBulkActionLoading(true);
        const results = await runWithConcurrency(jobs, BULK_CONCURRENCY, async (job) => {
            try {
                const res = await crewPlanApi.ChangeLabour(job.task.barcode, job.employeeId, job.task.wo_barcode);
                return res?.status === true;
            } catch {
                return false;
            }
        });
        setBulkActionLoading(false);

        const succeeded = results.filter(Boolean).length;
        const failed = results.length - succeeded;
        const affectedTaskBarcodes = new Set(
            jobs.filter((_, i) => results[i]).map(j => j.task.barcode)
        );

        if (failed === 0) {
            const parts: string[] = [
                `Successfully removed ${bulkRemoveEmployeeIds.size} employee(s) from ${affectedTaskBarcodes.size} task(s).`
            ];
            if (skippedTaskCount > 0) {
                parts.push(`Some selected tasks were skipped because they cannot be edited (${skippedTaskCount}).`);
            }
            showSnackbar(parts.join(" "), "success");
        } else {
            showSnackbar(
                `Could not update labour for ${failed} of ${jobs.length} removal(s). Please review and try again.`,
                "warning"
            );
        }

        setBulkRemoveDialogOpen(false);
        setBulkRemoveEmployeeIds(new Set());
        clearSelectedTasks();
        refreshWorkpackage(selectedEvent.id!.toString());
    };

    const submitClearAllLabour = async () => {
        type Job = { task: MxiTask, employeeId: number };
        const jobs: Job[] = [];
        for (const task of tasksInWorkpackage) {
            if (!canEditTaskLabour(task)) continue;
            for (const emp of task.assigned_employees ?? []) {
                jobs.push({ task, employeeId: emp.employee_id });
            }
        }

        if (jobs.length === 0) {
            showSnackbar("There is no assigned labour to clear.", "info");
            setClearAllLabourDialogOpen(false);
            return;
        }

        setBulkActionLoading(true);
        const results = await runWithConcurrency(jobs, BULK_CONCURRENCY, async (job) => {
            try {
                const res = await crewPlanApi.ChangeLabour(job.task.barcode, job.employeeId, job.task.wo_barcode);
                return res?.status === true;
            } catch {
                return false;
            }
        });
        setBulkActionLoading(false);

        const failed = results.filter(r => !r).length;
        if (failed === 0) {
            showSnackbar("All assigned labour was cleared from this work package.", "success");
        } else {
            showSnackbar(
                `Could not clear ${failed} of ${jobs.length} assignment(s). Please review and try again.`,
                "warning"
            );
        }

        setClearAllLabourDialogOpen(false);
        clearSelectedTasks();
        refreshWorkpackage(selectedEvent.id!.toString());
    };

    function onCloseDialog() {
        onClose();
    }

    // Derive aircraft type from the first task, since all tasks in a workpackage belong to the same aircraft
    const isBoeingTask = isBoeingAircraft(selectedEvent.inventory ?? "");

    const generateTableBody = (row: MxiTask) => {
        const isRowEditable = canEditTaskLabour(row);
        const isRowSelected = selectedTaskBarcodes.has(row.barcode);
        const taskPriorityLabel = formatTaskPriority(row.task_priority);
        return (
            <TableRow>
                {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            size="small"
                            checked={isRowSelected}
                            disabled={!isRowEditable}
                            onChange={() => toggleTaskSelection(row.barcode)}
                            inputProps={{ 'aria-label': `Select task ${row.mxi_task_id}` }}
                        />
                    </TableCell>
                )}
                <TableCell className="w-px" wrap={false}>
                    <div className="flex items-center gap-1">
                        <ActionChip
                            label="MXI"
                            onClick={() => redirectToMxi(row.barcode)}
                            style={{ backgroundColor: ReturnStatusColor(row.status, row.unassigned), ...getStatusBorderProperties(row.status) }}
                        />
                        {row.part_amounts > 0 && <div onClick={() => { setPartDialogOpen(true); setSelectedTask(row); }}>
                            <Tooltip title={`${row.part_amounts} parts`} type='primary' placement="top-start">
                                <Badge badgeContent={row.part_amounts} color="primary" >
                                    <ShoppingCart className="cursor-pointer m-auto" color={row.part_not_available ? 'error' : 'primary'} />
                                </Badge>
                            </Tooltip>
                        </div>
                        }
                        {row.need_cert && <div> <Tooltip title={`Certification required`} type='primary' placement="top-start"><Visibility /></Tooltip></div>}
                    </div>
                </TableCell>
                <TableCell className="w-px" wrap={false}>
                    {(row.status !== "COMPLETE" && row.status !== "HEADER" && row.unassigned === false && location !== "SHOPS" && !isBoeingTask) && (
                        <ActionChip label="eSign" color="primary" onClick={() => redirectToIdmrTask(row.wo_barcode, row.barcode)} />
                    )}
                </TableCell>
                <TableCell className="w-[30%]" style={{ textDecoration: row.unassigned === true ? "line-through" : undefined }}>{row.task_name}</TableCell>
                <TableCell>
                    {taskPriorityLabel !== "" && (
                        <div className="w-[25px] h-[25px] bg-red-500 text-white font-bold text-base flex items-center justify-center rounded-full text-center shrink-0">
                            {taskPriorityLabel}
                        </div>
                    )}
                </TableCell>
                <TableCell>{row.sub_class}</TableCell>
                <TableCell>{row.task_start_time}</TableCell>
                <RemarksCell notes={notesMap[row.barcode] || []} loaded={notesLoaded} />
                <TableCell>{row.action_items_counter}</TableCell>
                <TableCell className="w-[22%] align-middle" onClick={() => { onLabourColumnClicked(row) }}>
                    <div className="flex flex-wrap items-center gap-1 py-0.5">
                        {row.assigned_employees.map(emp => (
                            <Chip
                                key={emp.task_labour_assigned_id}
                                size="small"
                                color="primary"
                                label={emp.employee_name}
                            />
                        ))}
                    </div>
                </TableCell>
            </TableRow>
        )
    }

    return (
        <div>

            <Dialog
                fullScreen={true}
                show_options={false}
                strict={false}
                isSlide={true}
                max_width={'xl'}
                onClose={() => onCloseDialog()}
                title={""}
                visible={dialogOpen}
                context={
                    <div>

                        <Typography text={selectedEvent?.title} align='center' variant='h2' />
                        <div className="flex flex-wrap justify-center">
                            <Typography text={`${selectedEvent?.start_date_text} - ${selectedEvent?.end_date_text}`} />
                        </div>
                        <div className="flex flex-wrap justify-center">
                            <Typography text={selectedEvent?.wo_status} fontWeight='bold' margin={[1, 0, 0, 0]} />
                        </div>
                        <div className="flex flex-row flex-wrap justify-center gap-2 items-center flex-nowrap">
                            <div>
                                <IconButton onClick={() => redirectToMxi(selectedEvent?.id)} className="text-xs px-2 py-1 mr-12">MXI</IconButton>
                            </div>
                            {(selectedEvent?.wo_status !== "COMPLETE" && selectedEvent?.wo_status !== "HEADER" && location !== "SHOPS" && !isBoeingTask) && (
                                <div>
                                    <IconButton onClick={() => redirectToIdmrWorkPackage(String(selectedEvent?.id ?? ''))} className="text-xs px-2 py-1">eSign</IconButton>
                                </div>
                            )}
                        </div>
                        <StatusFilterChips tasks={tasksInWorkpackage} selected={statusFilters} onToggle={toggleStatusFilter} onClear={clearStatusFilters} />
                        <div>

                            {isAdmin && (
                                <div
                                    className="mt-2 mb-2 flex flex-wrap items-center gap-3 rounded-lg bg-black/[0.04] px-3 py-2"
                                >
                                    <label className="flex items-center gap-1 cursor-pointer select-none">
                                        <Checkbox
                                            size="small"
                                            checked={allEditableSelected}
                                            indeterminate={selectedTaskCount > 0 && !allEditableSelected}
                                            onChange={handleSelectAllToggle}
                                            disabled={editableTasks.length === 0 || bulkActionLoading}
                                            inputProps={{ 'aria-label': 'Select all editable tasks' }}
                                        />
                                        <span className="text-sm">
                                            {allEditableSelected ? "Deselect all" : "Select all tasks"}
                                        </span>
                                    </label>
                                    <span className="text-sm font-medium">
                                        {selectedTaskCount} selected
                                    </span>
                                    {selectedTaskCount > 0 && (
                                        <Button
                                            variant="text"
                                            color="secondary"
                                            size="small"
                                            onClick={clearSelectedTasks}
                                            disabled={bulkActionLoading}
                                        >
                                            Clear selection
                                        </Button>
                                    )}
                                    <div className="ml-auto flex flex-wrap items-center gap-2">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={openBulkAssignDialog}
                                            disabled={selectedTaskCount === 0 || bulkActionLoading}
                                        >
                                            Assign labour
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            onClick={openBulkRemoveDialog}
                                            disabled={
                                                selectedTaskCount === 0 ||
                                                assignedEmployeesInSelection.length === 0 ||
                                                bulkActionLoading
                                            }
                                        >
                                            Remove labour
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={openClearAllLabourDialog}
                                            disabled={!hasAnyAssignedLabour || bulkActionLoading}
                                        >
                                            Clear all labour
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <Table
                                // Remount on filter change: Table keeps its page in state and does not
                                // clamp it when the row count shrinks, which would leave a blank page.
                                key={Array.from(statusFilters).sort().join('|')}
                                displayEmptyText
                                emptyTableText="No tasks with this status"
                                headers={isAdmin
                                    ? ["", "", "Task Name", "Priority", "Sub class", "Start", "Remarks", "Action", "Assign labour"]
                                    : ["", "Task Name", "Priority", "Sub class", "Start", "Remarks", "Action", "Assign labour"]}
                                rows={visibleTasks}
                                generateBody={(row: MxiTask) => generateTableBody(row)}
                                defaultRowsPerPage={50}
                                pagination={true}
                            />
                            {(loading === true || bulkActionLoading === true) &&
                                <div>
                                    <br />
                                    <LinearProgress />
                                </div>
                            }
                        </div>
                    </div>
                }
            />
            {partDialogOpen === true && <Dialog
                fullScreen={false}
                show_options={false}
                strict={false}
                max_width={'lg'}
                onClose={() => setPartDialogOpen(false)}
                title={selectedTask!.task_name}
                visible={partDialogOpen}
                context={
                    <PartDetails taskBarcode={selectedTask!.barcode} />
                }
            />}
            {LabourDialogOpen === true && <Dialog
                fullScreen={false}
                show_options={false}
                strict={false}
                max_width={'lg'}
                onClose={() => setLabourDialogOpen(false)}
                title={selectedTask!.task_name}
                visible={LabourDialogOpen}
                context={
                    <div>
                        <div className='w-1/2'>
                            <EmployeeSearch place_holder='Search for employee' onSelectedEmployee={(id: number) => onSelectedEmployee(id)} />

                        </div>
                        <Collection edit={true} background={false} collectionList={selectedTask!.assigned_employees} title="Assigned Employees" nameProperty="employee_name" idProperty="employee_id" removeItem={(itemId: number) => onSelectedEmployee(itemId)} />
                    </div>
                }
            />}
            {bulkAssignDialogOpen && (
                <Dialog
                    fullScreen={false}
                    show_options={false}
                    strict={false}
                    max_width={'md'}
                    onClose={() => { if (!bulkActionLoading) setBulkAssignDialogOpen(false); }}
                    title={`Assign labour to ${selectedTaskCount} selected task(s)`}
                    visible={bulkAssignDialogOpen}
                    context={
                        <div>
                            <Typography
                                align="center"
                                text={`Search and add employees to assign to all ${selectedTaskCount} selected task(s).`}
                            />
                            <EmployeeSearch
                                place_holder="Search for employee"
                                fullUser={true}
                                clearOnSelect={true}
                                onSelectedEmployee={(user: User | null) => addEmployeeToBulkSelection(user)}
                            />
                            <Collection
                                edit={true}
                                background={false}
                                collectionList={bulkSelectedEmployees.map(u => ({
                                    // `id` is required because Collection internally
                                    // resolves the ProfileImg via `users.find(s => s.Id === item.id)`,
                                    // ignoring `idProperty` for that lookup.
                                    id: u.Id,
                                    employee_id: u.user_id,
                                    employee_name: u.name,
                                    user_image: u.user_image,
                                }))}
                                title={`Employees to assign (${bulkSelectedEmployees.length})`}
                                nameProperty="employee_name"
                                idProperty="employee_id"
                                removeItem={(employeeId: number) => removeEmployeeFromBulkSelection(employeeId)}
                            />
                            {bulkActionLoading && <LinearProgress />}
                            <div className="flex flex-wrap justify-end mt-2 gap-2">
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={() => setBulkAssignDialogOpen(false)}
                                    disabled={bulkActionLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={submitBulkAssignLabour}
                                    disabled={
                                        bulkActionLoading ||
                                        selectedTaskCount === 0 ||
                                        bulkSelectedEmployees.length === 0
                                    }
                                >
                                    Assign to selected tasks
                                </Button>
                            </div>
                        </div>
                    }
                />
            )}

            {bulkRemoveDialogOpen && (
                <Dialog
                    fullScreen={false}
                    show_options={false}
                    strict={false}
                    max_width={'md'}
                    onClose={() => { if (!bulkActionLoading) setBulkRemoveDialogOpen(false); }}
                    title={`Remove labour from ${selectedTaskCount} selected task(s)`}
                    visible={bulkRemoveDialogOpen}
                    context={
                        <div>
                            <Typography
                                align="center"
                                text={
                                    assignedEmployeesInSelection.length === 0
                                        ? "No employees are currently assigned to the selected tasks."
                                        : "Select the employees you want to remove from the selected tasks."
                                }
                            />
                            <List>
                                {assignedEmployeesInSelection.map(({ employee, taskCount }) => {
                                    const checked = bulkRemoveEmployeeIds.has(employee.employee_id);
                                    return (
                                        <ListItem
                                            key={employee.employee_id}
                                            className="cursor-pointer"
                                            onClick={() => toggleBulkRemoveEmployee(employee.employee_id)}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => toggleBulkRemoveEmployee(employee.employee_id)}
                                                inputProps={{ 'aria-label': `Remove ${employee.employee_name}` }}
                                            />
                                            <ListItemAvatar>
                                                <Avatar
                                                    className="h-7 w-7"
                                                    src={employee.employee_image}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={employee.employee_name}
                                                secondary={`Assigned to ${taskCount} selected task${taskCount === 1 ? '' : 's'}`}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                            {bulkActionLoading && <LinearProgress />}
                            <div className="flex flex-wrap justify-end mt-2 gap-2">
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={() => setBulkRemoveDialogOpen(false)}
                                    disabled={bulkActionLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={submitBulkRemoveLabour}
                                    disabled={
                                        bulkActionLoading ||
                                        selectedTaskCount === 0 ||
                                        bulkRemoveEmployeeIds.size === 0
                                    }
                                >
                                    Remove from selected tasks
                                </Button>
                            </div>
                        </div>
                    }
                />
            )}

            {clearAllLabourDialogOpen && (
                <Dialog
                    fullScreen={false}
                    show_options={false}
                    strict={false}
                    max_width={'sm'}
                    onClose={() => { if (!bulkActionLoading) setClearAllLabourDialogOpen(false); }}
                    title="Clear all labour from this work package"
                    visible={clearAllLabourDialogOpen}
                    context={
                        <div>
                            <Typography
                                align="center"
                                text="This will remove all assigned labour from every editable task in this work package. Non-editable tasks (e.g. headers) will be skipped."
                            />
                            <Typography
                                align="center"
                                text={`Tasks affected: ${clearableTasks.length}`}
                            />
                            <Typography
                                align="center"
                                text={`Labour assignments removed: ${totalClearableAssignments}`}
                            />
                            <Typography
                                align="center"
                                text="Are you sure you want to continue?"
                                fontWeight="bold"
                            />
                            {bulkActionLoading && <LinearProgress />}
                            <div className="flex flex-wrap justify-end mt-2 gap-2">
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={() => setClearAllLabourDialogOpen(false)}
                                    disabled={bulkActionLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={submitClearAllLabour}
                                    disabled={bulkActionLoading}
                                >
                                    Clear all labour
                                </Button>
                            </div>
                        </div>
                    }
                />
            )}

            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition="right"
                severity={snackbarSeverity}
            />
        </div>
    );
});
export default WorkPackageDetailsDialog;
